//#region ../src/storage/local-cache-byte-source.ts
/** Random-access reader over a complete, locally-held archive Blob. */
var LocalCacheByteSource = class {
	size;
	#blob;
	constructor(blob) {
		this.#blob = blob;
		this.size = blob.size;
	}
	async read(offset, length) {
		if (length <= 0) return new Uint8Array(0);
		const slice = this.#blob.slice(offset, offset + length);
		return new Uint8Array(await slice.arrayBuffer());
	}
};
/** In-memory store — the Node test/demo backing where `caches` is unavailable. */
var InMemoryLocalCacheStore = class {
	#map = /* @__PURE__ */ new Map();
	get(url) {
		return Promise.resolve(this.#map.get(url));
	}
	put(url, entry) {
		this.#map.set(url, entry);
		return Promise.resolve();
	}
	delete(url) {
		this.#map.delete(url);
		return Promise.resolve();
	}
};
/**
* Ask the browser to exempt this origin's storage from automatic eviction —
* best-effort, returns whether persistence is (now) granted. Deliberately a
* SEPARATE, explicit call rather than a side effect of `CacheApiStore.put`:
* in Firefox `storage.persist()` can raise a permission prompt, which must
* happen at one deliberate moment a caller chooses (e.g. when a warm-download
* begins), never on every cache write mid-flow.
*/
async function requestPersistentStorage() {
	try {
		return await navigator.storage?.persist?.() ?? false;
	} catch {
		return false;
	}
}
/**
* Cache API store (browser). `cache.put` only exposes an entry once its body
* has been fully consumed, and a caller that re-parses a cached copy before
* trusting it (evicting a poisoned one) needs no extra write-then-promote
* dance here. Eviction protection is the caller's move: call
* `requestPersistentStorage()` once at a deliberate moment — `put` itself
* never triggers a permission prompt.
*
* Exercised in Node with stubbed `caches`/`navigator` globals; the real Cache
* API behavior is proven by the TourViewer e2e suite.
*/
var CacheApiStore = class {
	#cacheName;
	constructor(cacheName = "range-archives") {
		this.#cacheName = cacheName;
	}
	async get(url) {
		const res = await (await caches.open(this.#cacheName)).match(url);
		if (!res) return void 0;
		const etag = res.headers.get("etag");
		const lastModified = res.headers.get("last-modified");
		const validators = etag !== null || lastModified !== null ? {
			...etag !== null ? { etag } : {},
			...lastModified !== null ? { lastModified } : {}
		} : void 0;
		return {
			blob: await res.blob(),
			...validators !== void 0 ? { validators } : {}
		};
	}
	async put(url, entry) {
		const cache = await caches.open(this.#cacheName);
		const headers = new Headers();
		if (entry.validators?.etag !== void 0) headers.set("etag", entry.validators.etag);
		if (entry.validators?.lastModified !== void 0) headers.set("last-modified", entry.validators.lastModified);
		await cache.put(url, new Response(entry.blob, { headers }));
	}
	async delete(url) {
		await (await caches.open(this.#cacheName)).delete(url);
	}
};
//#endregion
export { CacheApiStore, InMemoryLocalCacheStore, LocalCacheByteSource, requestPersistentStorage };
